import { Component } from "@theme/component";
import { formatMoney } from "@theme/discount-manager";
import { createLogger, createReporter } from "@theme/logger";
import { MysaEvents } from "@theme/mysa-events";
import { normalizeSectionId, sectionRenderer } from "@theme/section-renderer";
/**
 * @typedef {import("@theme/discount-manager").PricingDetails} PricingDetails
 */

/**
 * @typedef {Object} PriceBreakdownRefs
 * @property {HTMLElement} [checkoutValue] - The checkout price value element
 * @property {HTMLElement} [checkoutDescription] - The "At checkout" label
 * @property {HTMLElement} [byobChip] - The BYOB discount chip (hidden by default)
 * @property {HTMLElement} [msrpWrapper] - The MSRP price wrapper
 * @property {HTMLElement} [preByobPrice] - Pre-BYOB intermediate price wrapper
 * @property {HTMLElement} [preByobValue] - Pre-BYOB intermediate price value
 * @property {HTMLElement} [postPurchaseBonus] - The post-purchase bonus group (e.g. ON HRS, BC Hydro, Hilo)
 * @property {HTMLElement} [postPurchaseBonusAmount] - After-rebate price value (Save on Energy)
 */

/**
 * MysaPriceBreakdown - Displays product pricing with discount breakdown
 *
 * Architecture:
 * - Liquid renders all discount chips (general, utility, BC Hydro/Hilo bonus)
 * - Liquid renders BYOB chip (hidden by default)
 * - Section Rendering API morphs content on utility/variant changes
 * - Component base class handles ref tracking and DOM updates
 *
 * JS responsibilities:
 * - Morph section when utility partner changes
 * - Toggle BYOB chip visibility when quantity reaches a tier
 * - Update checkout price (affected by BYOB)
 * - Show/hide MSRP and pre-BYOB intermediate price
 * - Refresh Save on Energy after-rebate price on discount updates
 * - Animate visibility changes with GSAP
 */

const log = createLogger("PriceBreakdown");
const reportError = createReporter("PriceBreakdown");

/**
 * @extends {Component<PriceBreakdownRefs>}
 */
class MysaPriceBreakdown extends Component {
  constructor() {
    super();
    this.isInitialRender = true;
    this.handleDiscountUpdate = this.#onDiscountUpdate.bind(this);
    this.handleUtilityChange = this.#onUtilityChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Find parent section ID for section morphing
    const sectionElement =
      this.closest("[data-section-id]") ||
      this.closest(".shopify-section[id]") ||
      this.closest("section[id]");

    const sectionHTMLElement = /** @type {HTMLElement | null} */ (
      sectionElement
    );
    let sectionId =
      sectionHTMLElement?.dataset?.sectionId || sectionHTMLElement?.id;
    if (sectionId?.startsWith("shopify-section-")) {
      sectionId = sectionId.replace("shopify-section-", "");
    }
    this.sectionId = sectionId;

    this.#setupEventListeners();
  }

  /**
   * ON HRS fixed mail-in rebate in cents (0 = not eligible). Read live from
   * `data-hrs-rebate-amount-cents` so section morphs pick up utility changes.
   * @returns {number}
   */
  get #hrsRebateAmountCents() {
    return parseInt(this.dataset.hrsRebateAmountCents ?? "", 10) || 0;
  }

  #setupEventListeners() {
    document.addEventListener(
      MysaEvents.buyboxDiscountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      MysaEvents.utilityPartnerChanged,
      this.handleUtilityChange,
    );
    document.addEventListener(
      MysaEvents.utilityPartnerCleared,
      this.handleUtilityChange,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      MysaEvents.buyboxDiscountUpdate,
      this.handleDiscountUpdate,
    );
    document.removeEventListener(
      MysaEvents.utilityPartnerChanged,
      this.handleUtilityChange,
    );
    document.removeEventListener(
      MysaEvents.utilityPartnerCleared,
      this.handleUtilityChange,
    );
  }

  /**
   * Called by Component base class after Section Rendering API updates
   * Refs are automatically re-cached by Component's MutationObserver
   */
  updatedCallback() {
    super.updatedCallback();
    log("Section updated, refs re-cached by Component");
  }

  /**
   * Handle utility partner change events
   * Morphs the section to update all elements (price breakdown, utility block, etc.)
   */
  async #onUtilityChange() {
    if (!this.sectionId) {
      log("No section ID, cannot morph");
      return;
    }

    log("Utility changed, morphing section:", this.sectionId);

    try {
      const html = await sectionRenderer.renderSection(
        normalizeSectionId(this.sectionId),
        { cache: false },
      );
      if (html == null) return;

      requestAnimationFrame(() => {
        const section =
          document.getElementById(`shopify-section-${this.sectionId}`) ||
          this.closest(".shopify-section");
        if (section) {
          section.dispatchEvent(
            new CustomEvent("shopify:section:load", { bubbles: true }),
          );
          log("Section morphed and section:load dispatched");
        }
      });
    } catch (err) {
      console.error("Error morphing section:", err);
      reportError(err);
    }
  }

  /**
   * Handle discount update events
   * @param {Event} event - The discount update event
   */
  #onDiscountUpdate(event) {
    const customEvent = /** @type {CustomEvent<PricingDetails>} */ (event);
    const pricing = customEvent.detail;

    // Utility bundle: discount chips are static from Liquid, only update checkout price
    if (pricing.isUtilityBundle) {
      const unitCheckoutPrice = Math.round(
        pricing.discountedTotal / pricing.quantity,
      );
      log(
        `Bundle qty update - qty: ${pricing.quantity}, checkout: $${(unitCheckoutPrice / 100).toFixed(2)}`,
      );
      if (this.refs.checkoutValue) {
        this.refs.checkoutValue.textContent = formatMoney(unitCheckoutPrice);
      }
      return;
    }

    this.updateUI(pricing);
  }

  /**
   * Update the price breakdown UI with new discount data
   * @param {PricingDetails} pricing - Pricing details from DiscountManager
   */
  updateUI(pricing) {
    // Per-unit checkout price of a discounted unit (excludes units
    // reserved for a post-purchase rebate, which keep full price)
    const unitCheckoutPrice =
      pricing.displayUnitPrice ??
      Math.round(pricing.discountedTotal / pricing.quantity);

    log(
      `updateUI - byobDiscount: ${pricing.byobDiscount}, checkoutPrice: $${(unitCheckoutPrice / 100).toFixed(2)}`,
    );

    // Update checkout price (refs auto-tracked by Component)
    if (this.refs.checkoutValue) {
      this.refs.checkoutValue.textContent = formatMoney(unitCheckoutPrice);
    }

    // Toggle BYOB chip visibility
    this.#updateByobChip(pricing);
    this.#updatePostPurchaseBonus(pricing);
    this.#syncCheckoutDescription(pricing);

    // Mark initial render complete
    if (this.isInitialRender) {
      this.isInitialRender = false;
    }
  }

  /**
   * Toggle post-purchase bonus visibility and refresh Save on Energy
   * after-rebate price. The ON HRS rebate reserves a single unit of
   * smart-thermostat per cart; when a cart line already consumes that
   * slot the bonus must hide. Other post-purchase bonuses (BC Hydro, Hilo)
   * have no slot semantics, so `byobReservedQuantity === 0` for those.
   * @param {PricingDetails} pricing - Pricing details
   */
  #updatePostPurchaseBonus(pricing) {
    const bonus = this.refs.postPurchaseBonus;
    if (!bonus) return;

    const reserved = pricing.byobReservedQuantity || 0;
    const hrsAmount = this.#hrsRebateAmountCents;
    const slotCartQty = pricing.byobReservedSlotCartQuantity || 0;
    const pageReserved = Math.max(0, reserved - slotCartQty);
    const rebateActive = hrsAmount > 0 && (reserved <= 0 || pageReserved > 0);

    if (rebateActive && this.refs.postPurchaseBonusAmount) {
      this.refs.postPurchaseBonusAmount.textContent = formatMoney(
        this.#reservedUnitAfterRebateCents(pricing, hrsAmount),
      );
    }

    // ON HRS shows its own "After instant rebate" descriptor below
    // the checkout price; `#syncCheckoutDescription` hides "At checkout".

    if (reserved <= 0) return;

    if (slotCartQty >= reserved) {
      this.#animateOut(bonus);
    } else {
      this.#animateIn(bonus);
    }
  }

  /**
   * After-rebate price for the single reserved unit (MSRP minus general
   * discount minus the fixed mail-in rebate). BYOB does not apply to
   * this unit, so full-line checkout must not be used here.
   * @param {PricingDetails} pricing
   * @param {number} hrsAmount
   * @returns {number}
   */
  #reservedUnitAfterRebateCents(pricing, hrsAmount) {
    const generalPerUnit =
      pricing.quantity > 0
        ? (pricing.generalDiscount || 0) / pricing.quantity
        : 0;
    const reservedUnitCheckout = (pricing.originalPrice || 0) - generalPerUnit;
    return Math.max(0, reservedUnitCheckout - hrsAmount);
  }

  /**
   * "At checkout" is redundant when the displayed price already includes
   * an instant utility discount (rebate or Mysa), or when HRS after-rebate
   * copy is showing, or when there is no discount at all.
   * @param {PricingDetails} pricing
   */
  #syncCheckoutDescription(pricing) {
    const desc = this.refs.checkoutDescription;
    if (!desc) return;

    const hrsAmount = this.#hrsRebateAmountCents;
    const reserved = pricing.byobReservedQuantity || 0;
    const slotCartQty = pricing.byobReservedSlotCartQuantity || 0;
    const pageReserved = Math.max(0, reserved - slotCartQty);
    const hrsVisible = hrsAmount > 0 && (reserved <= 0 || pageReserved > 0);

    const utilityRebate = pricing.utilityRebate || 0;
    const utilityMysa = pricing.utilityMysa || 0;
    const hasDiscount =
      (pricing.byobDiscount || 0) > 0 ||
      (pricing.generalDiscount || 0) > 0 ||
      utilityRebate > 0 ||
      utilityMysa > 0;

    const hide =
      !hasDiscount || utilityRebate > 0 || utilityMysa > 0 || hrsVisible;

    if (hide) {
      desc.setAttribute("hidden", "");
    } else {
      desc.removeAttribute("hidden");
    }
  }

  /**
   * Animate element visible with GSAP (or instant if initial render)
   * @param {Element | null | undefined} el - Element to show
   * @param {string} [display="block"] - CSS display value
   */
  #animateIn(el, display = "block") {
    if (!el) return;
    const htmlEl = /** @type {HTMLElement} */ (el);
    if (!htmlEl.hasAttribute("hidden")) return;

    htmlEl.removeAttribute("hidden");
    if (typeof gsapGrowIn === "function" && !this.isInitialRender) {
      gsapGrowIn(htmlEl, { duration: 0.3, display });
    }
  }

  /**
   * Animate element hidden with GSAP (or instant if initial render)
   * @param {Element | null | undefined} el - Element to hide
   */
  #animateOut(el) {
    if (!el) return;
    const htmlEl = /** @type {HTMLElement} */ (el);
    if (htmlEl.hasAttribute("hidden")) return;

    if (typeof gsapGrowOut === "function" && !this.isInitialRender) {
      gsapGrowOut(htmlEl, {
        duration: 0.25,
        hideAfter: false,
        onComplete: () => htmlEl.setAttribute("hidden", ""),
      });
    } else {
      htmlEl.setAttribute("hidden", "");
    }
  }

  /**
   * Toggle BYOB chip visibility and related elements
   * @param {PricingDetails} pricing - Pricing details
   */
  #updateByobChip(pricing) {
    const byobChip = this.refs.byobChip;
    if (!byobChip) return;

    const hasByob = pricing.byobDiscount > 0;
    const hasOtherDiscounts =
      pricing.generalDiscount > 0 ||
      pricing.utilityRebate > 0 ||
      pricing.utilityMysa > 0;

    if (hasByob) {
      // Show MSRP (animate if it was hidden)
      this.#animateIn(this.refs.msrpWrapper);

      // Show pre-BYOB price when there are other discounts
      if (
        hasOtherDiscounts &&
        this.refs.preByobPrice &&
        this.refs.preByobValue
      ) {
        const unitCheckoutPrice =
          pricing.displayUnitPrice ??
          Math.round(pricing.discountedTotal / pricing.quantity);
        const byobPerUnit =
          pricing.byobPerUnit ??
          Math.round(pricing.byobDiscount / pricing.quantity);
        this.refs.preByobValue.textContent = formatMoney(
          unitCheckoutPrice + byobPerUnit,
        );
        this.#animateIn(this.refs.preByobPrice);
      } else {
        // Hide pre-BYOB price when other discounts are removed
        this.#animateOut(this.refs.preByobPrice);
      }

      this.#animateIn(byobChip, "flex");
      log("BYOB chip shown");
      return;
    }

    // Hide BYOB chip first, then cascade up to MSRP
    this.#animateOut(byobChip);
    this.#animateOut(this.refs.preByobPrice);

    // Hide MSRP only if no other discounts
    // Cascade: animate out after BYOB chip (staggered by 100ms)
    if (!hasOtherDiscounts) {
      if (!this.isInitialRender && typeof gsapGrowOut === "function") {
        setTimeout(() => {
          this.#animateOut(this.refs.msrpWrapper);
        }, 100);
      } else {
        this.refs.msrpWrapper?.setAttribute("hidden", "");
      }
    }

    log("BYOB chip hidden");
  }
}

if (!customElements.get("mysa-price-breakdown")) {
  customElements.define("mysa-price-breakdown", MysaPriceBreakdown);
}
